import type { ReactNode } from "react"

import { cn } from "@productlane/lib/src/browser"
import { formatRelativeTime } from "@productlane/lib/src/date"

import { SYSTEM_EVENT_RAIL_CLASS } from "./slack-style-message-surface"
import { Tooltip } from "./tooltip"

interface SystemEventProps {
	icon: ReactNode
	timestamp?: Date
	tooltipLabel?: string
	children: ReactNode
	hasEventAbove?: boolean
	hasEventBelow?: boolean
	className?: string
}

export function SystemEvent({
	icon,
	timestamp,
	tooltipLabel,
	children,
	hasEventAbove = false,
	hasEventBelow = false,
	className,
}: SystemEventProps) {
	return (
		<div
			className={cn(
				"flex min-w-0 cursor-default items-center gap-2.5 py-0 pr-0 text-xs",
				SYSTEM_EVENT_RAIL_CLASS,
				className,
			)}
		>
			<div className="flex w-8 min-w-8 flex-col items-center">
				<div
					className={cn(
						"h-[5px] w-[0.5px]",
						hasEventAbove
							? "bg-gray-300 dark:bg-gray-500"
							: "bg-gradient-to-t from-gray-300 to-transparent dark:from-gray-600",
					)}
				/>
				<div className="bg-background flex items-center justify-center rounded-full p-[2px]">
					{icon}
				</div>
				<div
					className={cn(
						"h-[5px] w-[0.5px]",
						hasEventBelow
							? "bg-gray-300 dark:bg-gray-500"
							: "bg-gradient-to-b from-gray-300 to-transparent dark:from-gray-600",
					)}
				/>
			</div>
			<div className="flex min-w-0 items-center gap-[5px]">
				<div className="flex min-w-0 items-center gap-1 overflow-hidden truncate max-2xl:max-w-80">
					{children}
				</div>
				{timestamp && (
					<>
						<span className="text-tertiary">&middot;</span>
						<Tooltip label={tooltipLabel || timestamp.toLocaleString()}>
							<div className="whitespace-nowrap text-tertiary">
								{formatRelativeTime(timestamp, true)}
							</div>
						</Tooltip>
					</>
				)}
			</div>
		</div>
	)
}
